import {
  DUPLICATE_OFFSET,
  alignOffset,
  boundsOfRects,
  compareIndex,
  createGroup,
  distributeOffsets,
  fitToRect,
  keysBetween,
  layoutMindmap,
  nodeAABB,
  rectCenter,
  rectContainsPoint,
  rectContainsRect,
  selectionBounds,
  tidyGridOffsets,
  type AlignAxis,
  type BoardNode,
  type ConnectorEndpoint,
  type FrameNode,
  type NodeId,
  type Rect,
  type ShapeStyle,
  type TextStyle,
  type Vec,
} from '@kanvas/board-core';
import type { BoardDoc } from '../collab/BoardDoc';
import type { Identity } from '../lib/identity';
import { animateViewportTo } from '../state/camera';
import { editorState, useEditor } from '../state/store';
import { cloneNodes, expandForClone } from './clone';

/** Everything an action needs; assembled once by the app shell. */
export interface ActionContext {
  doc: BoardDoc;
  identity: Identity;
}

function selected(doc: BoardDoc): BoardNode[] {
  return doc.getMany(editorState().selection);
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export function deleteSelection({ doc }: ActionContext): void {
  const ids = editorState().selection.filter((id) => !doc.get(id)?.locked);
  if (ids.length === 0) return;
  doc.remove(ids);
  useEditor.getState().clearSelection();
}

export function duplicateSelection(
  { doc, identity }: ActionContext,
  offset: Vec = DUPLICATE_OFFSET,
): void {
  const ids = editorState().selection;
  if (ids.length === 0) return;
  const source = expandForClone(ids, doc.all(), doc.lookup);
  if (source.length === 0) return;

  const indices = doc.topIndices(source.length);
  const { nodes, idMap } = cloneNodes(source, {
    offset,
    indices,
    userId: identity.userId,
  });
  doc.addMany(nodes);
  reparentIntoFrames({ doc, identity }, nodes.map((n) => n.id));
  useEditor.getState().select(ids.map((id) => idMap.get(id) ?? id));
}

export function selectAll({ doc }: ActionContext): void {
  const ids = doc
    .all()
    .filter((n) => !n.locked && n.parentId === null && n.type !== 'group')
    .map((n) => n.id);
  useEditor.getState().select(ids);
}

export function selectSameType({ doc }: ActionContext): void {
  const first = selected(doc)[0];
  if (!first) return;
  useEditor
    .getState()
    .select(doc.all().filter((n) => n.type === first.type && !n.locked).map((n) => n.id));
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export function groupSelection({ doc, identity }: ActionContext): void {
  const nodes = selected(doc).filter((n) => n.type !== 'frame');
  if (nodes.length < 2) return;

  const bounds = boundsOfRects(nodes.map((n) => nodeAABB(n, doc.lookup)));
  const group = createGroup(
    nodes.map((n) => n.id),
    bounds,
    { userId: identity.userId, index: doc.topIndex() },
  );

  doc.transact(() => {
    doc.nodes.set(group.id, group);
    for (const node of nodes) {
      doc.nodes.set(node.id, { ...node, parentId: group.id, updatedAt: Date.now() });
    }
  });
  useEditor.getState().select([group.id]);
}

export function ungroupSelection({ doc }: ActionContext): void {
  const groups = selected(doc).filter((n) => n.type === 'group');
  if (groups.length === 0) return;

  const freed: NodeId[] = [];
  doc.transact(() => {
    for (const group of groups) {
      if (group.type !== 'group') continue;
      for (const childId of group.children) {
        const child = doc.get(childId);
        if (!child) continue;
        doc.nodes.set(childId, { ...child, parentId: group.parentId, updatedAt: Date.now() });
        freed.push(childId);
      }
      doc.nodes.delete(group.id);
    }
  });
  useEditor.getState().select(freed);
}

// ---------------------------------------------------------------------------
// Arrange
// ---------------------------------------------------------------------------

/**
 * Translation patch for one node. Attached connector ends stay attached; only
 * free ends and bends travel.
 */
function translatePatch(node: BoardNode, d: Vec): Partial<BoardNode> {
  if (node.type === 'connector') {
    const shift = (ep: ConnectorEndpoint): ConnectorEndpoint =>
      ep.kind === 'point' ? { kind: 'point', x: ep.x + d.x, y: ep.y + d.y } : ep;
    return {
      start: shift(node.start),
      end: shift(node.end),
      bend: node.bend.map((b) => ({ x: b.x + d.x, y: b.y + d.y })),
    } as Partial<BoardNode>;
  }
  return { x: node.x + d.x, y: node.y + d.y } as Partial<BoardNode>;
}

/**
 * Moves a node together with everything that belongs to it — group members
 * transitively, frame contents — the same set a drag takes along. Align,
 * distribute, tidy and the arrow-key nudge all go through here; patching only
 * the selected box left grouped members behind.
 */
export function translateNodes(
  doc: BoardDoc,
  nodes: readonly { node: BoardNode; delta: Vec }[],
  patches: Map<NodeId, Partial<BoardNode>> = new Map(),
): Map<NodeId, Partial<BoardNode>> {
  const visit = (node: BoardNode, delta: Vec) => {
    if (patches.has(node.id) || node.locked) return;
    patches.set(node.id, translatePatch(node, delta));
    if (node.type === 'group') {
      for (const childId of node.children) {
        const child = doc.get(childId);
        if (child) visit(child, delta);
      }
    } else if (node.type === 'frame') {
      for (const child of frameChildren(doc, node.id)) visit(child, delta);
    }
  };
  for (const { node, delta } of nodes) {
    if (delta.x === 0 && delta.y === 0) continue;
    visit(node, delta);
  }
  return patches;
}

export function alignSelection({ doc }: ActionContext, axis: AlignAxis): void {
  const nodes = selected(doc).filter((n) => !n.locked && n.type !== 'connector');
  if (nodes.length < 2) return;

  const rects = nodes.map((n) => nodeAABB(n, doc.lookup));
  const target = boundsOfRects(rects);
  const patches = translateNodes(
    doc,
    nodes.map((node, i) => ({ node, delta: alignOffset(rects[i]!, target, axis) })),
  );
  if (patches.size > 0) doc.updateMany(patches);
}

export function distributeSelection({ doc }: ActionContext, axis: 'x' | 'y'): void {
  const nodes = selected(doc).filter((n) => !n.locked && n.type !== 'connector');
  if (nodes.length < 3) return;

  const rects = nodes.map((n) => nodeAABB(n, doc.lookup));
  const offsets = distributeOffsets(rects, axis);
  const patches = translateNodes(
    doc,
    nodes.map((node, i) => ({ node, delta: offsets[i]! })),
  );
  if (patches.size > 0) doc.updateMany(patches);
}

export function tidySelection({ doc }: ActionContext, gap = 24): void {
  const nodes = selected(doc).filter((n) => !n.locked && n.type !== 'connector');
  if (nodes.length < 2) return;

  const rects = nodes.map((n) => nodeAABB(n, doc.lookup));
  const offsets = tidyGridOffsets(rects, gap);
  const patches = translateNodes(
    doc,
    nodes.map((node, i) => ({ node, delta: offsets[i]! })),
  );
  if (patches.size > 0) doc.updateMany(patches);
}

/**
 * Lays out the tree reachable from the current selection's single node,
 * following node-to-node connectors as parent-to-child edges. Positions land
 * in one transaction, so it is one undo step regardless of tree size.
 */
export function autoLayoutMindmap(
  { doc }: ActionContext,
  options: { direction?: 'right' | 'down' } = {},
): void {
  const rootId = editorState().selection[0];
  if (!rootId || editorState().selection.length !== 1) return;
  const root = doc.get(rootId);
  if (!root || root.type === 'connector' || root.type === 'group' || root.locked) return;

  const all = doc.all();
  const edges = new Map<NodeId, NodeId[]>();
  for (const n of all) {
    if (n.type !== 'connector') continue;
    if (n.start.kind !== 'node' || n.end.kind !== 'node') continue;
    const list = edges.get(n.start.nodeId) ?? [];
    list.push(n.end.nodeId);
    edges.set(n.start.nodeId, list);
  }

  const boxes = new Map<NodeId, { w: number; h: number }>();
  for (const n of all) {
    if (n.type !== 'connector' && n.type !== 'group' && !n.locked) boxes.set(n.id, { w: n.w, h: n.h });
  }

  const positions = layoutMindmap(boxes, edges, rootId, { x: root.x, y: root.y }, options);
  if (positions.size === 0) return;

  const patches = new Map<NodeId, Partial<BoardNode>>();
  positions.forEach((pos, id) => patches.set(id, { x: pos.x, y: pos.y }));
  doc.updateMany(patches);
}

export function reorderSelection(
  { doc }: ActionContext,
  target: 'front' | 'back' | 'forward' | 'backward',
): void {
  const ids = editorState().selection;
  if (ids.length === 0) return;
  doc.reorder(ids, target);
}

export function setLocked({ doc }: ActionContext, locked: boolean): void {
  const ids = editorState().selection;
  if (ids.length === 0) return;
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const id of ids) patches.set(id, { locked });
  doc.updateMany(patches);
  if (locked) useEditor.getState().clearSelection();
}

/** Hidden nodes stay in the document but stop painting and stop being pickable. */
export function setHidden(doc: BoardDoc, ids: readonly NodeId[], hidden: boolean): void {
  if (ids.length === 0) return;
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const id of ids) patches.set(id, { hidden });
  doc.updateMany(patches);
  if (hidden) {
    const editor = useEditor.getState();
    editor.select(editor.selection.filter((id) => !ids.includes(id)));
  }
}

export function setTags({ doc }: ActionContext, tags: string[]): void {
  const ids = editorState().selection;
  if (ids.length === 0) return;
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const id of ids) patches.set(id, { tags });
  doc.updateMany(patches);
}

export function unlockAll({ doc }: ActionContext): void {
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of doc.all()) if (node.locked) patches.set(node.id, { locked: false });
  if (patches.size > 0) doc.updateMany(patches);
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

export function applyShapeStyle({ doc }: ActionContext, patch: Partial<ShapeStyle>): void {
  const nodes = selected(doc);
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of nodes) {
    if (node.locked) continue;
    if (node.type === 'shape') {
      patches.set(node.id, { style: { ...node.style, ...patch } } as Partial<BoardNode>);
    } else if (node.type === 'connector') {
      // Connectors share the stroke controls with shapes.
      const connectorPatch: Partial<BoardNode> = {};
      if (patch.stroke !== undefined) (connectorPatch as { stroke?: string }).stroke = patch.stroke;
      if (patch.strokeWidth !== undefined) {
        (connectorPatch as { strokeWidth?: number }).strokeWidth = patch.strokeWidth;
      }
      if (patch.strokeStyle !== undefined) {
        (connectorPatch as { strokeStyle?: ShapeStyle['strokeStyle'] }).strokeStyle =
          patch.strokeStyle;
      }
      if (Object.keys(connectorPatch).length > 0) patches.set(node.id, connectorPatch);
    } else if (node.type === 'sticky' && patch.fill !== undefined) {
      patches.set(node.id, { fill: patch.fill } as Partial<BoardNode>);
    }
  }
  if (patches.size > 0) doc.updateMany(patches);
  // Also update the tool defaults so the next object matches.
  useEditor.getState().patchShapeStyle(patch);
}

export function applyTextStyle({ doc }: ActionContext, patch: Partial<TextStyle>): void {
  const nodes = selected(doc);
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of nodes) {
    if (node.locked) continue;
    if (
      node.type === 'shape' ||
      node.type === 'sticky' ||
      node.type === 'text' ||
      node.type === 'connector'
    ) {
      patches.set(node.id, {
        textStyle: { ...node.textStyle, ...patch },
      } as Partial<BoardNode>);
    }
  }
  if (patches.size > 0) doc.updateMany(patches);
  useEditor.getState().patchTextStyle(patch);
}

export function setStickyFill(ctx: ActionContext, fill: string): void {
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of selected(ctx.doc)) {
    if (node.type === 'sticky' && !node.locked) {
      patches.set(node.id, { fill } as Partial<BoardNode>);
    }
  }
  if (patches.size > 0) ctx.doc.updateMany(patches);
  useEditor.getState().setStickyFill(fill);
}

export function setOpacity({ doc }: ActionContext, opacity: number): void {
  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of selected(doc)) if (!node.locked) patches.set(node.id, { opacity });
  if (patches.size > 0) doc.updateMany(patches);
}

/** Copies the style of the first selected node onto the rest. */
export function copyStyleToSelection({ doc }: ActionContext): void {
  const nodes = selected(doc);
  const source = nodes[0];
  if (!source || nodes.length < 2) return;

  const patches = new Map<NodeId, Partial<BoardNode>>();
  for (const node of nodes.slice(1)) {
    if (node.locked || node.type !== source.type) continue;
    if (source.type === 'shape' && node.type === 'shape') {
      patches.set(node.id, {
        style: { ...source.style },
        textStyle: { ...source.textStyle },
      } as Partial<BoardNode>);
    } else if (source.type === 'sticky' && node.type === 'sticky') {
      patches.set(node.id, {
        fill: source.fill,
        textStyle: { ...source.textStyle },
      } as Partial<BoardNode>);
    } else if (source.type === 'connector' && node.type === 'connector') {
      patches.set(node.id, {
        stroke: source.stroke,
        strokeWidth: source.strokeWidth,
        strokeStyle: source.strokeStyle,
        routing: source.routing,
        startHead: source.startHead,
        endHead: source.endHead,
      } as Partial<BoardNode>);
    } else if (source.type === 'text' && node.type === 'text') {
      patches.set(node.id, { textStyle: { ...source.textStyle } } as Partial<BoardNode>);
    } else if (source.type === 'draw' && node.type === 'draw') {
      patches.set(node.id, {
        color: source.color,
        size: source.size,
        brush: source.brush,
      } as Partial<BoardNode>);
    }
  }
  if (patches.size > 0) doc.updateMany(patches);
}

// ---------------------------------------------------------------------------
// Frames and parenting
// ---------------------------------------------------------------------------

/**
 * Innermost frame this node should belong to, or null to sit on the board.
 *
 * Shapes and stickies must be fully inside — a straddling note would flicker
 * between frames. Images attach when their centre lands in the frame: a photo
 * is almost always larger than the frame it was dropped onto, and the strict
 * test left every picture unparented.
 */
export function frameParentFor(doc: BoardDoc, node: BoardNode): NodeId | null {
  if (node.type === 'frame' || node.type === 'group') return null;
  if (node.parentId) {
    const parent = doc.get(node.parentId);
    if (parent?.type === 'group') return node.parentId;
  }

  const aabb = nodeAABB(node, doc.lookup);
  const byCenter = node.type === 'image';
  const center = rectCenter(aabb);
  let best: { id: NodeId; area: number } | null = null;

  for (const frame of doc.all()) {
    if (frame.type !== 'frame') continue;
    const box: Rect = { x: frame.x, y: frame.y, w: frame.w, h: frame.h };
    const inside = byCenter ? rectContainsPoint(box, center) : rectContainsRect(box, aabb);
    if (!inside) continue;
    const area = box.w * box.h;
    if (!best || area < best.area) best = { id: frame.id, area };
  }
  return best?.id ?? null;
}

/**
 * Re-parents nodes into whichever frame now contains them.
 *
 * A child that ends up underneath its frame is raised just above the frame
 * fill, otherwise a newly drawn frame hides the picture it was meant to hold.
 */
export function reparentIntoFrames({ doc }: ActionContext, ids: readonly NodeId[]): void {
  const frames = doc.all().filter((n) => n.type === 'frame');
  if (frames.length === 0) return;

  const patches = new Map<NodeId, Partial<BoardNode>>();
  const raise = new Map<NodeId, NodeId[]>();

  for (const id of ids) {
    const node = doc.get(id);
    if (!node || node.type === 'frame' || node.type === 'group') continue;
    const nextParent = frameParentFor(doc, node);
    if (node.parentId === nextParent) continue;

    patches.set(id, { parentId: nextParent });
    if (!nextParent) continue;
    const frame = doc.get(nextParent);
    if (frame && compareIndex(node, frame) < 0) {
      const bucket = raise.get(nextParent);
      if (bucket) bucket.push(id);
      else raise.set(nextParent, [id]);
    }
  }

  for (const [frameId, childIds] of raise) {
    const frame = doc.get(frameId);
    if (!frame) continue;
    const raising = new Set(childIds);
    const ceiling = doc
      .all()
      .filter((n) => !raising.has(n.id) && compareIndex(frame, n) < 0)
      .sort(compareIndex)[0]?.index;
    const ordered = childIds
      .map((id) => doc.get(id))
      .filter((n): n is BoardNode => n !== undefined)
      .sort(compareIndex);
    const keys = keysBetween(frame.index, ceiling ?? null, ordered.length);
    ordered.forEach((node, i) => {
      const patch = patches.get(node.id) ?? {};
      patches.set(node.id, { ...patch, index: keys[i] });
    });
  }

  if (patches.size > 0) doc.updateMany(patches);
}

/** Nodes that should move together with a frame. */
export function frameChildren(doc: BoardDoc, frameId: NodeId): BoardNode[] {
  const frame = doc.get(frameId);
  if (!frame || frame.type !== 'frame') return [];
  const box: Rect = { x: frame.x, y: frame.y, w: frame.w, h: frame.h };
  return doc.all().filter((n) => {
    if (n.id === frameId) return false;
    if (n.parentId === frameId) return true;
    if (frameParentFor(doc, n) === frameId) return true;
    // Nested frames and groups are never parented; they still travel when enclosed.
    return rectContainsRect(box, nodeAABB(n, doc.lookup));
  });
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

export function zoomToSelection({ doc }: ActionContext): void {
  const nodes = selected(doc);
  if (nodes.length === 0) return;
  const bounds = selectionBounds(nodes, doc.lookup);
  const vp = editorState().viewport;
  animateViewportTo(fitToRect(vp, bounds, { padding: 96, maxZoom: 2 }));
}

export function zoomToFit({ doc }: ActionContext): void {
  const vp = editorState().viewport;
  const nodes = doc.all().filter((n) => n.type !== 'group' && !n.hidden);
  if (nodes.length === 0) {
    animateViewportTo({ zoom: 1, x: -vp.width / 2, y: -vp.height / 2 });
    return;
  }
  const bounds = boundsOfRects(nodes.map((n) => nodeAABB(n, doc.lookup)));
  animateViewportTo(fitToRect(vp, bounds, { padding: 80, maxZoom: 1 }));
}

export function zoomToNode({ doc }: ActionContext, id: NodeId, maxZoom = 1): void {
  const node = doc.get(id);
  if (!node) return;
  const bounds = nodeAABB(node, doc.lookup);
  const vp = editorState().viewport;
  animateViewportTo(fitToRect(vp, bounds, { padding: 48, maxZoom }));
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

/**
 * Frames in slide order: explicitly numbered frames first (by `slideIndex`),
 * then any frame that has not been added to the deck, in canvas z-order.
 * That means presenting never has to be "set up" first — every frame is a
 * slide from the moment it exists, and numbering is only for reordering.
 */
export function presentationFrames(doc: BoardDoc): FrameNode[] {
  const frames = doc.all().filter((n): n is FrameNode => n.type === 'frame');
  return frames.sort((a, b) => {
    const ai = a.slideIndex ?? Number.MAX_SAFE_INTEGER;
    const bi = b.slideIndex ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return compareIndex(a, b);
  });
}

export function startPresentation(doc: BoardDoc, fromFrameId?: NodeId): boolean {
  const frames = presentationFrames(doc);
  if (frames.length === 0) return false;
  const start = (fromFrameId ? frames.find((f) => f.id === fromFrameId) : undefined) ?? frames[0]!;
  presentFrame(doc, start.id, { immediate: true });
  return true;
}

export function exitPresentation(): void {
  useEditor.getState().setUi({ presentingFrameId: null });
}

export function presentFrame(
  doc: BoardDoc,
  frameId: NodeId,
  options: { immediate?: boolean } = {},
): void {
  const frame = doc.get(frameId);
  if (!frame || frame.type !== 'frame') return;
  useEditor.getState().setUi({ presentingFrameId: frameId });
  const bounds = nodeAABB(frame, doc.lookup);
  const vp = editorState().viewport;
  animateViewportTo(fitToRect(vp, bounds, { padding: 0, maxZoom: 4 }), {
    immediate: options.immediate,
  });
}

/** Steps to the next (`1`) or previous (`-1`) slide, wrapping around the deck. */
export function presentStep(doc: BoardDoc, direction: 1 | -1): void {
  const frames = presentationFrames(doc);
  const currentId = useEditor.getState().presentingFrameId;
  if (frames.length === 0 || currentId === null) return;
  const at = frames.findIndex((f) => f.id === currentId);
  const next = frames[(at + direction + frames.length) % frames.length]!;
  presentFrame(doc, next.id);
}

/** Adds, removes or reorders a frame within the presentation deck. */
export function setFramePresentationOrder(
  doc: BoardDoc,
  frameId: NodeId,
  action: 'add' | 'remove' | 'up' | 'down',
): void {
  const frame = doc.get(frameId);
  if (!frame || frame.type !== 'frame') return;
  const ordered = presentationFrames(doc).filter((f) => f.slideIndex !== null);

  if (action === 'add') {
    if (frame.slideIndex !== null) return;
    const next = ordered.length > 0 ? Math.max(...ordered.map((f) => f.slideIndex!)) + 1 : 0;
    doc.update<FrameNode>(frameId, { slideIndex: next });
    return;
  }
  if (action === 'remove') {
    doc.update<FrameNode>(frameId, { slideIndex: null });
    // Compact the remaining numbers so gaps do not accumulate over time.
    ordered.filter((f) => f.id !== frameId).forEach((f, i) => doc.update<FrameNode>(f.id, { slideIndex: i }));
    return;
  }
  const at = ordered.findIndex((f) => f.id === frameId);
  if (at === -1) return;
  const swapWith = action === 'up' ? at - 1 : at + 1;
  if (swapWith < 0 || swapWith >= ordered.length) return;
  const a = ordered[at]!;
  const b = ordered[swapWith]!;
  doc.update<FrameNode>(a.id, { slideIndex: b.slideIndex });
  doc.update<FrameNode>(b.id, { slideIndex: a.slideIndex });
}

// ---------------------------------------------------------------------------
// Undo / redo
// ---------------------------------------------------------------------------

export function undo({ doc }: ActionContext): void {
  doc.undoManager.undo();
}

export function redo({ doc }: ActionContext): void {
  doc.undoManager.redo();
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Splits multi-line text on a sticky into one sticky per line. */
export function explodeSticky({ doc, identity }: ActionContext): void {
  const nodes = selected(doc).filter((n) => n.type === 'sticky');
  if (nodes.length === 0) return;

  doc.transact(() => {
    for (const node of nodes) {
      if (node.type !== 'sticky') continue;
      const lines = node.text.split('\n').map((l) => l.trim()).filter((l) => l !== '');
      if (lines.length < 2) continue;

      // Bounded above by the next node in paint order: an open upper bound
      // generated keys that collided with, or jumped over, whatever sat
      // above the sticky.
      const ordered = doc.all();
      const above = ordered[ordered.findIndex((n) => n.id === node.id) + 1];
      const indices = keysBetween(node.index, above?.index ?? null, lines.length);
      doc.nodes.delete(node.id);
      lines.forEach((line, i) => {
        const copy = cloneNodes([node], {
          offset: { x: (node.w + 20) * i, y: 0 },
          indices: [indices[i]!],
          userId: identity.userId,
        });
        const clone = copy.nodes[0]!;
        doc.nodes.set(clone.id, { ...clone, text: line } as BoardNode);
      });
    }
  });
  useEditor.getState().clearSelection();
}
